import { Link } from 'react-router-dom';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { Icon } from '../../components/ui/Icon';
import { useConfig } from '../../lib/config';
import { FREE_PLAN, PLANS, designManifest, formatPlanPrice } from '@sarnia/shared';
import './marketing.css';
import './marketingPrice.css';

export function HomePage() {
  const { billingEnabled } = useConfig();
  const templateCount = designManifest.length;
  // A varied strip: every other design, so old and new styles are mixed.
  const templates = designManifest.filter((_, i) => i % 2 === 0).map((entry) => `${entry.id}.jpg`);

  return (
    <PublicLayout>
      <section className="mk-hero">
        <div className="sarnia-container">
          <div className="mk-hero__inner">
            <p className="mk-hero__eyebrow">Small island. Big signal.</p>
            <h1 className="mk-hero__title">Build a website you're proud of, in an afternoon.</h1>
            <p className="mk-hero__lead">
              Pick a beautiful Guernsey-made template, edit it visually, share a marked preview for
              feedback, then export anywhere or get help going live.
            </p>
            <div className="mk-hero__actions">
              <Link className="sarnia-btn sarnia-btn--primary sarnia-btn--lg" to="/signup">
                {billingEnabled ? 'Start your free trial' : 'Start building'}
              </Link>
              <Link className="sarnia-btn sarnia-btn--secondary sarnia-btn--lg" to="/templates">
                Browse templates
              </Link>
            </div>
          </div>
          <div className="mk-hero__mock">
            <div className="mk-browser">
              <div className="mk-browser__bar">
                <span className="mk-browser__dots" aria-hidden="true">
                  <span className="mk-browser__dot mk-browser__dot--red" />
                  <span className="mk-browser__dot mk-browser__dot--yellow" />
                  <span className="mk-browser__dot mk-browser__dot--green" />
                </span>
                <span className="mk-browser__address">your-site.sarnia.uk</span>
              </div>
              <img className="mk-browser__img" src="/template-previews/design-01.jpg" alt="Sarnia Builder design template preview" loading="lazy" />
            </div>
            <div className="mk-mock-float mk-mock-float--1">
              <img src="/template-previews/design-05.jpg" alt="Template preview one" loading="lazy" />
            </div>
            <div className="mk-mock-float mk-mock-float--2">
              <img src="/template-previews/design-13.jpg" alt="Template preview two" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      <section className="mk-proof">
        <div className="sarnia-container mk-proof__inner">
          <span className="mk-proof__item"><Icon name="check" size={16} /> {templateCount} designer templates</span>
          <span className="mk-proof__item"><Icon name="zap" size={16} /> Live in minutes</span>
          <span className="mk-proof__item"><Icon name="sparkles" size={16} /> Free during early access</span>
        </div>
      </section>

      <section className="mk-section">
        <div className="sarnia-container">
          <div className="mk-section__header">
            <h2 className="mk-section__title">How it works</h2>
          </div>
          <div className="mk-steps__grid">
            <div className="mk-step">
              <div className="mk-step__icon"><Icon name="layout" size={24} /></div>
              <h3 className="mk-step__title">Pick a template or start blank</h3>
              <p className="mk-step__text">Choose from {templateCount} designer templates or begin with a clean canvas.</p>
            </div>
            <div className="mk-step">
              <div className="mk-step__icon"><Icon name="mouse" size={24} /></div>
              <h3 className="mk-step__title">Click to edit</h3>
              <p className="mk-step__text">Click any text, image or block and edit it in place — no code required.</p>
            </div>
            <div className="mk-step">
              <div className="mk-step__icon"><Icon name="rocket" size={24} /></div>
              <h3 className="mk-step__title">Share, export or go live</h3>
              <p className="mk-step__text">Share a preview link, download a ZIP to host anywhere, or choose a plan and we'll take it live.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="mk-section mk-section--alt">
        <div className="sarnia-container">
          <div className="mk-section__header">
            <h2 className="mk-section__title">Built for your business</h2>
            <p className="mk-section__lede">Everything you need to look sharp, load fast, and feel in control.</p>
          </div>
          <div className="mk-features__grid">
            <div className="mk-feature">
              <div className="mk-feature__icon"><Icon name="edit" size={20} /></div>
              <h3 className="mk-feature__title">Visual editing</h3>
              <p className="mk-feature__text">Click any text or image on the page and edit it in place. No guesswork.</p>
            </div>
            <div className="mk-feature">
              <div className="mk-feature__icon"><Icon name="palette" size={20} /></div>
              <h3 className="mk-feature__title">Designer templates</h3>
              <p className="mk-feature__text">Start with a polished, responsive design crafted by a Guernsey studio.</p>
            </div>
            <div className="mk-feature">
              <div className="mk-feature__icon"><Icon name="moon" size={20} /></div>
              <h3 className="mk-feature__title">Light &amp; dark mode</h3>
              <p className="mk-feature__text">Your site adapts beautifully to system preferences with one toggle.</p>
            </div>
            <div className="mk-feature">
              <div className="mk-feature__icon"><Icon name="book" size={20} /></div>
              <h3 className="mk-feature__title">Blank canvas with a guided tutorial</h3>
              <p className="mk-feature__text">Start from scratch with an in-editor walkthrough that keeps you moving.</p>
            </div>
            <div className="mk-feature">
              <div className="mk-feature__icon"><Icon name="upload" size={20} /></div>
              <h3 className="mk-feature__title">Export anywhere</h3>
              <p className="mk-feature__text">Download your whole site as a ZIP and host it anywhere, or let us take it live.</p>
            </div>
            <div className="mk-feature">
              <div className="mk-feature__icon"><Icon name="user" size={20} /></div>
              <h3 className="mk-feature__title">Live help from real people</h3>
              <p className="mk-feature__text">On a paid plan we get your site live on your domain and keep it maintained.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="mk-price-section">
        <div className="sarnia-container">
          <div className="mk-price__header">
            <h2 className="mk-section__title">Everything is free. Pay only if you want us to handle setup and maintenance.</h2>
          </div>
          <div className="mk-price__grid">
            <div className="mk-price__tile">
              <div className="mk-price__plan">{FREE_PLAN.name}</div>
              <div className="mk-price__price">
                £0
                <span className="mk-price__period">/mo</span>
              </div>
              <p className="mk-price__tagline">{FREE_PLAN.tagline}</p>
            </div>
            {PLANS.map((plan) => (
              <div
                className={`mk-price__tile ${plan.highlight ? 'mk-price__tile--highlight' : ''}`}
                key={plan.id}
              >
                <div className="mk-price__plan">{plan.name}</div>
                <div className="mk-price__price">
                  {formatPlanPrice(plan)}
                  <span className="mk-price__period">/mo</span>
                </div>
                <p className="mk-price__tagline">{plan.tagline}</p>
              </div>
            ))}
          </div>
          <div className="mk-price__actions">
            <Link className="sarnia-btn sarnia-btn--primary" to="/pricing">See pricing</Link>
          </div>
        </div>
      </section>

      <section className="mk-section">
        <div className="sarnia-container">
          <div className="mk-section__header">
            <h2 className="mk-section__title">Templates made to stand out</h2>
            <p className="mk-section__lede">Launch with a design that already looks like a local legend.</p>
          </div>
          <div className="mk-showcase__grid">
            {templates.map((filename) => (
              <Link key={filename} className="mk-showcase__item" to="/templates">
                <img src={`/template-previews/${filename}`} alt={`Template preview ${filename.replace('design-', '').replace('.jpg', '')}`} loading="lazy" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="mk-cta">
        <div className="sarnia-container">
          <div className="mk-cta__card">
            <h2 className="mk-cta__title">Ready to set sail?</h2>
            <p className="mk-cta__text">Join local businesses already building with Sarnia Builder.</p>
            <Link className="sarnia-btn sarnia-btn--primary sarnia-btn--lg" to="/signup">
              {billingEnabled ? 'Start free trial' : 'Start building'}
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
